import { createContext, useContext } from 'react';
import type {
  CreateWorkspaceCompanyInput,
  CreateWorkspaceCompanyResult,
  UpdateWorkspaceCompanyInput,
  UpdateWorkspaceCompanyResult,
  WorkspaceSummary,
} from '@tabbio/types';
import type { Workspace } from '../lib/sidebar';

export interface WorkspaceContextValue {
  workspaces: Workspace[];
  activeWorkspace: Workspace;
  activeWorkspaceId: string;
  switchWorkspace: (id: string) => Promise<Workspace>;
  createCompanyWorkspace: (
    input: CreateWorkspaceCompanyInput,
  ) => Promise<CreateWorkspaceCompanyResult>;
  updateCompanyWorkspace: (
    companyId: string,
    input: UpdateWorkspaceCompanyInput,
  ) => Promise<UpdateWorkspaceCompanyResult>;
  refetchWorkspaces: () => Promise<unknown>;
  mode: 'seeker' | 'employer';
  companyId: string | undefined;
  isLoading: boolean;
  isSwitching: boolean;
  isCreatingCompany: boolean;
  isUpdatingCompany: boolean;
  error: Error | null;
}

export const FALLBACK_WORKSPACE: WorkspaceSummary = {
  id: 'personal',
  label: 'Personal',
  type: 'personal',
  initial: 'P',
};

export const WorkspaceContext = createContext<WorkspaceContextValue>({
  workspaces: [FALLBACK_WORKSPACE],
  activeWorkspace: FALLBACK_WORKSPACE,
  activeWorkspaceId: 'personal',
  switchWorkspace: async () => FALLBACK_WORKSPACE,
  createCompanyWorkspace: async () => {
    throw new Error('WorkspaceProvider is missing');
  },
  updateCompanyWorkspace: async () => {
    throw new Error('WorkspaceProvider is missing');
  },
  refetchWorkspaces: async () => undefined,
  mode: 'seeker',
  companyId: undefined,
  isLoading: true,
  isSwitching: false,
  isCreatingCompany: false,
  isUpdatingCompany: false,
  error: null,
});

export function useWorkspace() {
  return useContext(WorkspaceContext);
}
